import { AsyncPipe } from '@angular/common';
import { Component, inject, Input } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { RouterLink } from '@angular/router';
import { AuthenticationSandbox } from '@app/authentication/sandboxes/authentication.sandbox';
import { FilingOptionsInputComponent } from '@app/services/filing-options/components/filing-options-input/filing-options-input.component';
import { WaterFinalInvoiceServiceStore } from '@app/services/water-final-invoice/store/water-final-invoice-service.store';
import { addLeaverRoute } from '@app/real-estate-agent/constants/route.constants';
import { WaterDetailFormElementComponent } from '@app/services/water-transfer-document/components/water-detail-form-element/water-detail-form-element.component';
import { WaterTransferDocumentServiceStore } from '@app/services/water-transfer-document/store/water-transfer-document-service.store';
import { appI18nKeyTypes } from '@app/shared/constants/i18n-key-type-map';
import { TranslateModule } from '@ngx-translate/core';
import { UiContext } from '@smooved/ui';
import { StatusBadgeStatus } from '@ui-v2/atoms/status-badge/status-badge.constants';
import { AddressInputComponent } from '@ui/form/address-input/address-input.component';
import { InlineSVGModule } from 'ng-inline-svg-2';
import { DbUtils } from '@core/utils';
import { WaterCompositeStore } from '@app/services/water-composite/store/water-composite.store';

@Component({
    selector: 'app-leaver-form',
    standalone: true,
    imports: [
        FormsModule,
        TranslateModule,
        ReactiveFormsModule,
        InlineSVGModule,
        MatSlideToggle,
        AddressInputComponent,
        RouterLink,
        AsyncPipe,
        WaterDetailFormElementComponent,
        FilingOptionsInputComponent,
    ],
    template: `
        <div [formGroup]="parentForm">
            <app-water-detail-form-element
                [showServiceStatusIcon]="showServiceStatusIcon"
                [serviceStatusIcon]="serviceStatusIcon"
                [title]="'V2.USERS.LEAVER.TITLE'"
            >
                @if (leaverPresent) {
                    @if (authenticationSandbox.isAdmin$ | async) {
                        <app-filing-options-input
                            class="u-display-block u-margin-bottom"
                            [supplierId]="DbUtils.getStringId(waterCompositeStore.waterTransferDocumentService()?.supplier)"
                            [serviceTypeId]="waterCompositeStore.waterFinalInvoiceService()?.serviceTypeId"
                            formControlName="leaverFilingOptionId"
                        ></app-filing-options-input>
                    }
                    <mat-slide-toggle
                        id="invoice-by-post"
                        [checked]="false"
                        formControlName="leaverFinalInvoiceByPost"
                        color="primary"
                        ngDefaultControl
                        >{{ 'V2.COMMUNICATION.INVOICE_BY_POST' | translate }}
                    </mat-slide-toggle>

                    @if (parentForm.get('leaverFinalInvoiceByPost')?.value) {
                        <smvd-ui-address-input
                            class="u-display-block u-margin-top"
                            formControlName="leaverContactAddress"
                            ngDefaultControl
                        ></smvd-ui-address-input>
                    }
                } @else {
                    <div class="grid grid-cols-[auto_1fr_auto] gap-2 u-background-gray-lightest p-4 u-bordered">
                        <div class="text-icon-error" inlineSVG="https://assets.smooved.be/icons/v2/20/circle-alert-default.svg"></div>
                        <div>
                            <p class="u-font-weight-semi-bold">{{ 'V2.USERS.LEAVER.LEAVER_REQUIRED.TITLE' | translate }}</p>
                            <p>{{ 'V2.USERS.LEAVER.LEAVER_REQUIRED.CONTENT' | translate }}</p>
                        </div>
                        @if (!(authenticationSandbox.isAdmin$ | async)) {
                            <a [routerLink]="addLeaverRoute" class="u-text-underlined u-font-weight-normal">{{ 'ADD' | translate }}</a>
                        }
                    </div>
                }
            </app-water-detail-form-element>
        </div>
    `,
})
export class LeaverFormComponent {
    @Input() public parentForm: FormGroup;
    @Input() public leaverPresent: boolean;
    @Input() public showServiceStatusIcon: boolean = false;
    @Input() public serviceStatusIcon: StatusBadgeStatus;
    protected readonly uiContext = UiContext;
    protected readonly i18nKeys = appI18nKeyTypes;
    protected readonly addLeaverRoute = addLeaverRoute;

    public authenticationSandbox = inject(AuthenticationSandbox);
    public waterTransferDocumentServiceStore = inject(WaterTransferDocumentServiceStore);
    public waterCompositeStore = inject(WaterCompositeStore);
    protected readonly DbUtils = DbUtils;
}
